<template>
  <div ref="modal" class="modal polygon-editor">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-body">
          <header class="flex">
            <div class="flex-1">Add Site Polygon</div>
            <a @click="cancel()" class="btn btn-light-blue">Cancel</a>
            <a @click="save()" class="btn btn-blue">Save Site Polygon</a>
          </header>

          <google-map
            :height="450"
            :draggable="true"
            :linkedMap="editingPolygon ? formMap : null"
            :withMap="storePolyEditorMap"
            :hideFilterInfo="true"
            :zoom="15"
          >
            <template slot-scope="{ google, map }">
              <div v-for="polygon in polygons" :key="polygon.key">
                <keep-alive>
                  <polygon-editor
                    :google="google"
                    :map="map"
                    :polygon="polygon"
                    :active="isActivePolygon(polygon)"
                  />
                </keep-alive>
              </div>
            </template>
          </google-map>

          <br />

          <div class="flex">
            <div class="flex-1">
              <p>
                Click 3 times on the map to start building your polygon. Drag
                nodes to add or change node positions, or right-click nodes to
                remove them. When you're happy with the polygon, save the
                points.
              </p>
              <ul>
                <li v-for="(polygon, index) in polygons" :key="index">
                  <a class="btn btn-blue" @click="activatePolygon(polygon)">
                    <span v-show="isActivePolygon(polygon)"
                      >Currently Editing
                    </span>
                    Polygon {{ index + 1 }}
                  </a>
                </li>
                <li>
                  <a class="btn btn-blue" @click="addPolygon"
                    >+ Add another polygon</a
                  >
                </li>
              </ul>
            </div>

            <div style="width: 100px;"></div>

            <div>
              <p>
                Made a mistake?
              </p>
              <ul>
                <li>
                  <a class="btn btn-blue" @click="clearAll()"
                    >Clear All Site Points</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as turf from '@turf/turf';
import GoogleMap from '@/components/map/google-map';
import PolygonEditor from './polygon-editor';
import Polygon from '@/store/models/polygon';
import { mapState, mapGetters } from 'vuex';

export default {
  components: {
    GoogleMap,
    PolygonEditor,
  },

  props: {
    opened: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      polygons: [],
      activePolygon: undefined,
    };
  },

  watch: {
    opened: function(open) {
      open ? this.onOpen() : this.onClose();
    },
  },

  computed: {
    ...mapGetters(['coordinateMode', 'center']),
    ...mapState({
      geojson: (state) => state.coordinateEditor.geojson,
      editingPolygon: ({ coordinateEditor }) => coordinateEditor.editingPolygon,
      formMap: ({ coordinateEditor }) => coordinateEditor.formMap,
    }),
  },

  methods: {
    storePolyEditorMap(map) {
      this.$store.commit('setPolyEditorMap', map);
    },

    onOpen() {
      $(this.$refs.modal).modal('show');
      this.originalGeoJSON = this.geojson;

      if (this.coordinateMode == 'MultiPolygon') {
        this.polygons = this.geojson.geometry.coordinates.map((coords) => {
          return new Polygon(coords);
        });
      } else {
        this.polygons = []
      }

      if (this.polygons.length == 0) {
        this.addPolygon();
      }

      if (!this.activePolygon) {
        this.activatePolygon(this.polygons[0]);
      }
    },

    onClose() {
      $(this.$refs.modal).modal('hide');
      $('body').addClass('modal-open');
    },

    addPolygon() {
      let newPolygon = new Polygon();
      this.polygons.push(newPolygon);
      this.activePolygon = newPolygon;
    },

    activatePolygon(polygon) {
      this.activePolygon = polygon;
    },

    isActivePolygon(polygon) {
      return this.activePolygon ? (this.activePolygon.key === polygon.key) : false;
    },

    clearAll() {
      if (this.polygons.length > 0) {
        this.$store.commit('setGeoJSON', this.center);
        this.polygons = [];
      }
    },

    cancel() {
      this.$store.commit('setGeoJSON', this.originalGeoJSON);
      this.close();
    },

    close() {
      this.activePolygon = undefined
      this.$store.commit('editingPolygon', false);
    },

    // Convert all Polygons to MultiPolygon GeoJSON objects
    save() {
      let polygons = this.polygons.filter(poly => poly.isValid())

      if (polygons.length > 0) {
        this.$store.commit(
          'setGeoJSON',
          turf.multiPolygon(polygons.map(poly => poly.path))
        );
      }

      this.$store.commit('setCoordinates', this.center);
      this.close();
    },
  },
};
</script>

<style lang="scss" scoped>
header {
  padding-bottom: 1rem;
  align-items: center;

  a {
    margin-left: 1rem;
  }
}

ul {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    padding-bottom: 1rem;
  }
}
</style>
